// Shared KTX2Loader singleton. The asset compaction pipeline emits all
// material and env-map textures as Basis-Universal-compressed .ktx2 files,
// so plain THREE.TextureLoader / RGBELoader can't decode them — every site
// that used to instantiate one of those loaders now uses this one instead.
//
// KTX2Loader needs two pieces of setup before its first .load():
//   1. setTranscoderPath() — points at /basis/, where vite serves
//      basis_transcoder.{js,wasm} (copied from
//      node_modules/three/examples/jsm/libs/basis/).
//   2. detectSupport(renderer) — peeks at the renderer's GPU capabilities
//      and picks the best transcoded format (ASTC / BC7 / ETC2 / …).
//
// Lifecycle: the renderer is two-phase (`new Renderer(canvas)` then
// `await renderer.init()`), and the synchronous Renderer constructor
// already builds the MapArenaView, which builds materials, which call
// loadKtx2(...). All of that happens BEFORE init() runs, so the loader
// can't be ready yet — instead, every load() goes through `whenReady`:
// returns the singleton if init has run, otherwise queues on a promise
// that resolves the moment `initKtx2Loader(renderer)` fires.

import * as THREE from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import type { WebGPURenderer } from 'three/webgpu';

const TRANSCODER_PATH = '/basis/';

let loader: KTX2Loader | null = null;
let initialized = false;
let resolveReady: ((l: KTX2Loader) => void) | null = null;
let readyPromise: Promise<KTX2Loader> = new Promise((r) => { resolveReady = r; });

// In-flight loadKtx2() promises. We add to this set when a load is issued and
// remove on settle, so callers (Renderer.awaitAssetsReady) can block until the
// initial wave of texture loads has fully transcoded — without this, init()
// returns while materials still hold 1×1 placeholder textures and the first
// few frames render solid white.
const inFlight = new Set<Promise<unknown>>();

/** Configure the singleton with a renderer for GPU-format detection.
 *  Idempotent. Flushes any loads queued through `whenReady()` before
 *  init ran. */
export function initKtx2Loader(renderer: WebGPURenderer): KTX2Loader {
  if (!loader) {
    loader = new KTX2Loader().setTranscoderPath(TRANSCODER_PATH);
  }
  loader.detectSupport(renderer);
  if (!initialized) {
    initialized = true;
    resolveReady!(loader);
  }
  return loader;
}

/** Resolves with the configured loader. Pre-init callers (materials built
 *  inside the Renderer constructor) get queued; the queue flushes when
 *  initKtx2Loader runs. Post-init this is a synchronously-resolved promise. */
export function whenKtx2Ready(): Promise<KTX2Loader> {
  return readyPromise;
}

/** Returns a placeholder texture immediately and fills it in once the KTX2
 *  finishes transcoding off-thread. Mirrors THREE.TextureLoader's sync-
 *  placeholder ergonomics so material constructors can keep doing
 *    `const t = loadKtx2(url); t.colorSpace = ...; super({ map: t });`
 *  without restructuring around async/await — the GPU sees an empty 1x1
 *  during the transcode window, then needsUpdate=true triggers re-upload
 *  with the real compressed mipmaps. User-set props (colorSpace, wrapS,
 *  wrapT, repeat, anisotropy) are preserved; only the data-side fields
 *  (image, mipmaps, format, type, filters) are populated by the load. */
export function loadKtx2(url: string): THREE.CompressedTexture {
  const placeholder = new THREE.CompressedTexture([], 1, 1);
  // KTX2 ships with a full mip pyramid baked in by the asset compaction
  // pipeline — we copy `loaded.mipmaps` over below and never want three
  // to regenerate them on upload (regeneration both wastes GPU time AND
  // is unsupported on compressed textures, where it silently no-ops).
  // Setting the flag on the placeholder up-front means any frame that
  // uploads before the transcode completes also avoids the regen path.
  placeholder.generateMipmaps = false;
  const p = whenKtx2Ready().then((l) => new Promise<void>((resolve) => {
    l.load(url, (loaded) => {
      placeholder.image = loaded.image;
      placeholder.mipmaps = loaded.mipmaps;
      placeholder.format = loaded.format;
      placeholder.type = loaded.type;
      placeholder.minFilter = loaded.minFilter;
      placeholder.magFilter = loaded.magFilter;
      placeholder.generateMipmaps = false;
      placeholder.needsUpdate = true;
      loaded.dispose();
      resolve();
    }, undefined, (err) => {
      console.warn(`[ktx2Loader] failed to load ${url}:`, err);
      resolve();
    });
  }));
  inFlight.add(p);
  void p.finally(() => { inFlight.delete(p); });
  return placeholder;
}

/** Resolves once every in-flight loadKtx2() has settled. Loops until idle so
 *  loads spawned *during* the await (e.g. dependent material constructors)
 *  are also drained. Resolves immediately if nothing is pending. */
export async function whenKtx2Idle(): Promise<void> {
  while (inFlight.size > 0) {
    await Promise.allSettled([...inFlight]);
  }
}
